import { CONSENT_CATEGORIES, createDefaultConsentState } from './categories';

const COOKIEYES_SCRIPT_BASE = 'https://cdn-cookieyes.com/client_data';
const CONSENT_COOKIE_NAME = 'cookieyes-consent';
const CONSENT_DEBUG_STORAGE_KEY = 'mpweb-consent-debug';
const CONSENT_DEBUG_EVENT_NAME = 'mpweb_consent_debug_changed';
const FORCE_ALL_CONSENT = toBoolean(process.env.NEXT_PUBLIC_CONSENT_DEBUG_FORCE_ALL);
const COOKIEYES_EVENT_NAMES = [
    'cookieyes_consent_update',
    'cookieyes_consent_changed',
    'cookieyes_banner_load',
    'cookieyes_banner_loaded'
];

const CATEGORY_ALIASES = {
    [CONSENT_CATEGORIES.NECESSARY]: ['necessary', 'essential', 'required'],
    [CONSENT_CATEGORIES.FUNCTIONAL]: ['functional', 'preferences'],
    [CONSENT_CATEGORIES.ANALYTICS]: ['analytics', 'performance', 'statistics'],
    [CONSENT_CATEGORIES.MARKETING]: ['marketing', 'advertisement', 'advertising', 'targeting']
};

function toBoolean(value) {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value !== 'string') return false;

    return ['yes', 'true', '1', 'accepted', 'allow', 'allowed'].includes(value.toLowerCase());
}

function getCookieValue(cookieName) {
    if (typeof document === 'undefined') return null;

    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${cookieName}=`);
    if (parts.length !== 2) return null;

    return parts.pop().split(';').shift();
}

function parseConsentCookie() {
    const rawCookie = getCookieValue(CONSENT_COOKIE_NAME);
    if (!rawCookie) return null;

    const decoded = decodeURIComponent(rawCookie);
    const parts = decoded.split(',');
    const parsed = {};

    parts.forEach((part) => {
        const [rawKey, rawValue] = part.split(':');
        if (!rawKey || rawValue === undefined) return;
        parsed[rawKey.trim()] = rawValue.trim();
    });

    return parsed;
}

function readWindowConsentPayload() {
    if (typeof window === 'undefined') return null;

    return (
        window.CookieYes?.consent ||
        window.CookieYes?.data?.consent ||
        window.cookieyes?.consent ||
        window.ckyConsent ||
        null
    );
}

function resolveCategory(payload, categoryName) {
    const aliases = CATEGORY_ALIASES[categoryName] || [];

    for (const alias of aliases) {
        if (payload?.[alias] !== undefined) {
            return toBoolean(payload[alias]);
        }
    }

    return null;
}

function isDebugConsentAllowed() {
    if (typeof window === 'undefined') return false;

    const host = window.location.hostname;
    return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.test') || host.endsWith('.local');
}

function readDebugConsentOverride() {
    if (typeof window === 'undefined' || !isDebugConsentAllowed()) {
        return null;
    }

    const rawOverride = window.localStorage.getItem(CONSENT_DEBUG_STORAGE_KEY);
    if (!rawOverride) {
        return null;
    }

    try {
        const parsedOverride = JSON.parse(rawOverride);
        const state = createDefaultConsentState();

        state.functional = toBoolean(parsedOverride.functional);
        state.analytics = toBoolean(parsedOverride.analytics);
        state.marketing = toBoolean(parsedOverride.marketing);
        state.ready = true;
        state.source = 'debug';

        return state;
    } catch {
        return null;
    }
}

function createForcedConsentState() {
    const state = createDefaultConsentState();

    state.functional = true;
    state.analytics = true;
    state.marketing = true;
    state.ready = true;
    state.source = 'debug';

    return state;
}

export function getCookieYesScriptUrl() {
    const explicitUrl = process.env.NEXT_PUBLIC_COOKIEYES_SCRIPT_URL;
    if (explicitUrl) return explicitUrl;

    const clientId = process.env.NEXT_PUBLIC_COOKIEYES_CLIENT_ID;
    if (!clientId) return null;

    return `${COOKIEYES_SCRIPT_BASE}/${clientId}/script.js`;
}

export function isCookieYesConfigured() {
    return !!getCookieYesScriptUrl();
}

export function readCookieYesConsent() {
    if (FORCE_ALL_CONSENT) {
        return createForcedConsentState();
    }

    const debugOverride = readDebugConsentOverride();
    if (debugOverride) {
        return debugOverride;
    }

    const state = createDefaultConsentState();
    const payload = readWindowConsentPayload() || parseConsentCookie();

    if (!payload) {
        return state;
    }

    state.functional = resolveCategory(payload, CONSENT_CATEGORIES.FUNCTIONAL) ?? state.functional;
    state.analytics = resolveCategory(payload, CONSENT_CATEGORIES.ANALYTICS) ?? state.analytics;
    state.marketing = resolveCategory(payload, CONSENT_CATEGORIES.MARKETING) ?? state.marketing;
    state.ready = true;
    state.source = 'cookieyes';

    return state;
}

export function subscribeToCookieYes(onChange) {
    if (typeof window === 'undefined') {
        return () => {};
    }

    const notify = () => {
        onChange(readCookieYesConsent());
    };

    const handleStorageChange = (event) => {
        if (!event || event.key === CONSENT_DEBUG_STORAGE_KEY) {
            notify();
        }
    };

    COOKIEYES_EVENT_NAMES.forEach((eventName) => {
        window.addEventListener(eventName, notify);
        document.addEventListener(eventName, notify);
    });

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(CONSENT_DEBUG_EVENT_NAME, notify);

    const intervalId = window.setInterval(() => {
        notify();
    }, 1000);

    return () => {
        COOKIEYES_EVENT_NAMES.forEach((eventName) => {
            window.removeEventListener(eventName, notify);
            document.removeEventListener(eventName, notify);
        });

        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener(CONSENT_DEBUG_EVENT_NAME, notify);

        window.clearInterval(intervalId);
    };
}

export function setDebugConsentOverride(nextConsent) {
    if (typeof window === 'undefined' || !isDebugConsentAllowed()) {
        return false;
    }

    if (!nextConsent) {
        window.localStorage.removeItem(CONSENT_DEBUG_STORAGE_KEY);
        window.dispatchEvent(new Event(CONSENT_DEBUG_EVENT_NAME));
        return true;
    }

    const normalizedConsent = {
        functional: toBoolean(nextConsent.functional),
        analytics: toBoolean(nextConsent.analytics),
        marketing: toBoolean(nextConsent.marketing)
    };

    window.localStorage.setItem(CONSENT_DEBUG_STORAGE_KEY, JSON.stringify(normalizedConsent));
    window.dispatchEvent(new Event(CONSENT_DEBUG_EVENT_NAME));
    return true;
}